/**
 * Chat Panel Component
 * Slide-out panel for document Q&A with streaming responses
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import {
  XMarkIcon,
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import {
  HandThumbUpIcon as HandThumbUpSolidIcon,
  HandThumbDownIcon as HandThumbDownSolidIcon,
} from "@heroicons/react/24/solid";
import {
  sendChatMessage,
  getChatMessages,
  getSessionToken,
  clearSessionToken,
  getSuggestedQuestions,
  submitFeedback,
  getChatConfig,
  getIndexStatus,
} from "../../services/chat.service";
import type { UUID } from "../../types/common.types";
import type {
  ChatMessage,
  ChatCitation,
  FeedbackRating,
  ChatConfig,
  SuggestedQuestion,
} from "../../types/chat.types";
import LoadingSpinner from "../common/LoadingSpinner";

interface ChatPanelProps {
  contentItemId: UUID;
  isOpen: boolean;
  onClose: () => void;
  onPageJump?: (pageNumber: number) => void;
  config?: ChatConfig;
}

export default function ChatPanel({
  contentItemId,
  isOpen,
  onClose,
  onPageJump,
  config: externalConfig,
}: ChatPanelProps) {
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // State
  const [inputValue, setInputValue] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  // Get session token
  const sessionToken = getSessionToken(contentItemId);

  // Fetch chat config
  const { data: config } = useQuery({
    queryKey: ["chatConfig", contentItemId],
    queryFn: () => getChatConfig(contentItemId),
    enabled: !externalConfig && isOpen,
  });

  const chatConfig = externalConfig || config;

  // Fetch index status
  const { data: indexStatus } = useQuery({
    queryKey: ["indexStatus", contentItemId],
    queryFn: () => getIndexStatus(contentItemId),
    enabled: isOpen,
  });

  // Fetch existing messages
  const { data: existingMessages, isLoading: messagesLoading } = useQuery({
    queryKey: ["chatMessages", sessionToken],
    queryFn: () => getChatMessages(sessionToken),
    enabled: isOpen && !!sessionToken,
  });

  // Fetch suggested questions
  const { data: suggestedQuestions } = useQuery({
    queryKey: ["suggestedQuestions", contentItemId],
    queryFn: () => getSuggestedQuestions(contentItemId),
    enabled: isOpen && chatConfig?.show_top_questions !== false,
  });

  // Update messages when existing messages are loaded
  useEffect(() => {
    if (existingMessages) {
      setMessages(existingMessages);
    }
  }, [existingMessages]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, streamingContent]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Feedback mutation
  const feedbackMutation = useMutation({
    mutationFn: ({
      messageId,
      rating,
    }: {
      messageId: UUID;
      rating: FeedbackRating;
    }) => submitFeedback(messageId, rating),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chatMessages", sessionToken],
      });
    },
  });

  // Handle sending a message
  const handleSendMessage = useCallback(async () => {
    const message = inputValue.trim();
    if (!message || isStreaming) {
      return;
    }

    setInputValue("");
    setError(null);
    setIsStreaming(true);
    setStreamingContent("");

    // Add user message optimistically
    const userMessage: ChatMessage = {
      id: crypto.randomUUID() as UUID,
      session_id: "" as UUID,
      role: "user",
      content: message,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      await sendChatMessage(contentItemId, message, {
        stream: true,
        onChunk: (chunk) => {
          if (chunk.content) {
            setStreamingContent((prev) => prev + chunk.content);
          }
        },
        onComplete: (msg) => {
          setMessages((prev) => [...prev, msg]);
          setStreamingContent("");
          setIsStreaming(false);
        },
        onError: (err) => {
          setError(err.message);
          setIsStreaming(false);
          setStreamingContent("");
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send message");
      setIsStreaming(false);
      setStreamingContent("");
    }
  }, [inputValue, isStreaming, contentItemId]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  // Handle suggested question click
  const handleSuggestedQuestion = useCallback((question: string) => {
    setInputValue(question);
    inputRef.current?.focus();
  }, []);

  // Handle new conversation
  const handleNewConversation = useCallback(() => {
    clearSessionToken(contentItemId);
    setMessages([]);
    setError(null);
    setShowDisclaimer(true);
    queryClient.invalidateQueries({
      queryKey: ["chatMessages"],
    });
  }, [contentItemId, queryClient]);

  // Handle feedback
  const handleFeedback = useCallback(
    (messageId: UUID, rating: FeedbackRating) => {
      feedbackMutation.mutate({ messageId, rating });
      // Optimistically update the message
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, feedback_rating: rating } : msg,
        ),
      );
    },
    [feedbackMutation],
  );

  // Handle citation click
  const handleCitationClick = useCallback(
    (pageNumber: number) => {
      if (onPageJump) {
        onPageJump(pageNumber);
      }
    },
    [onPageJump],
  );

  // Check if document is ready for chat
  const isReady = indexStatus?.status === "ready";
  const isIndexing = indexStatus?.status === "indexing";

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={clsx(
        "fixed right-0 top-0 bottom-0 w-96 bg-white shadow-2xl z-50",
        "flex flex-col transition-transform duration-300",
        isOpen ? "translate-x-0" : "translate-x-full",
      )}
    >
      {/* Header */}
      <div className="flex-none flex items-center justify-between px-4 py-3 bg-gray-800 text-white">
        <div className="flex items-center gap-2">
          <ChatBubbleLeftRightIcon className="h-5 w-5" />
          <span className="font-medium">Ask about this document</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleNewConversation}
            className="p-1.5 hover:bg-gray-700 rounded-lg"
            title="New conversation"
          >
            <ArrowPathIcon className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-700 rounded-lg"
            title="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Disclaimer */}
      {showDisclaimer && chatConfig?.chat_disclaimer && (
        <div className="flex-none bg-amber-50 border-b border-amber-200 px-4 py-3">
          <div className="flex items-start gap-2">
            <InformationCircleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-amber-800">
                {chatConfig.chat_disclaimer}
              </p>
              <button
                onClick={() => setShowDisclaimer(false)}
                className="text-xs text-amber-600 hover:underline mt-1"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Messages */}
      {!isReady && (
        <div className="flex-none px-4 py-3 bg-gray-100 border-b">
          {isIndexing ? (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <LoadingSpinner size="sm" />
              <span>Preparing document for chat...</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <ExclamationCircleIcon className="h-5 w-5 text-gray-400" />
              <span>Chat is not available for this document yet.</span>
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messagesLoading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        ) : messages.length === 0 && !streamingContent ? (
          <div className="text-center py-8">
            <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">
              Ask a question about this document
            </p>

            {/* Suggested Questions */}
            {suggestedQuestions && suggestedQuestions.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-gray-400 uppercase tracking-wide">
                  Suggested questions
                </p>
                {suggestedQuestions.map((q: SuggestedQuestion) => (
                  <button
                    key={q.id}
                    onClick={() => handleSuggestedQuestion(q.question)}
                    className="block w-full text-left px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    {q.question}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <ChatMessageComponent
                key={message.id}
                message={message}
                onFeedback={handleFeedback}
                onCitationClick={handleCitationClick}
              />
            ))}

            {/* Streaming Message */}
            {isStreaming && streamingContent && (
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <ChatBubbleLeftRightIcon className="h-4 w-4 text-blue-600" />
                </div>
                <div className="flex-1 bg-gray-100 rounded-lg px-4 py-3">
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">
                    {streamingContent}
                    <span className="animate-pulse">|</span>
                  </p>
                </div>
              </div>
            )}

            {/* Loading indicator */}
            {isStreaming && !streamingContent && (
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <ChatBubbleLeftRightIcon className="h-4 w-4 text-blue-600" />
                </div>
                <div className="flex-1 bg-gray-100 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-2">
                    <LoadingSpinner size="sm" />
                    <span className="text-sm text-gray-500">Thinking...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex-none px-4 py-2 bg-red-50 border-t border-red-200">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Input */}
      <div className="flex-none border-t bg-white px-4 py-3">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question..."
            disabled={!isReady || isStreaming}
            className={clsx(
              "flex-1 resize-none rounded-lg border px-3 py-2 text-sm",
              "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
              "disabled:bg-gray-100 disabled:cursor-not-allowed",
              "min-h-[44px] max-h-32",
            )}
            rows={1}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || !isReady || isStreaming}
            className={clsx(
              "flex-shrink-0 p-2 rounded-lg transition-colors",
              inputValue.trim() && isReady && !isStreaming
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-200 text-gray-400 cursor-not-allowed",
            )}
          >
            <PaperAirplaneIcon className="h-5 w-5" />
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2 text-center">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}

// =====================================================
// CHAT MESSAGE COMPONENT
// =====================================================

interface ChatMessageComponentProps {
  message: ChatMessage;
  onFeedback?: (messageId: UUID, rating: FeedbackRating) => void;
  onCitationClick?: (pageNumber: number) => void;
}

function ChatMessageComponent({
  message,
  onFeedback,
  onCitationClick,
}: ChatMessageComponentProps) {
  const isUser = message.role === "user";

  return (
    <div className={clsx("flex gap-3", isUser && "flex-row-reverse")}>
      {/* Avatar */}
      <div
        className={clsx(
          "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
          isUser ? "bg-gray-800" : "bg-blue-100",
        )}
      >
        {isUser ? (
          <span className="text-white text-xs font-medium">You</span>
        ) : (
          <ChatBubbleLeftRightIcon className="h-4 w-4 text-blue-600" />
        )}
      </div>

      {/* Message Content */}
      <div
        className={clsx(
          "flex-1 rounded-lg px-4 py-3",
          isUser ? "bg-gray-800 text-white" : "bg-gray-100",
        )}
      >
        <p
          className={clsx(
            "text-sm whitespace-pre-wrap",
            isUser ? "text-white" : "text-gray-800",
          )}
        >
          {message.content}
        </p>

        {/* Citations */}
        {!isUser && message.citations && message.citations.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-2">Sources:</p>
            <div className="flex flex-wrap gap-1">
              {message.citations.map(
                (citation: ChatCitation, index: number) => (
                  <button
                    key={citation.chunk_id || index}
                    onClick={() =>
                      citation.page_number &&
                      onCitationClick?.(citation.page_number)
                    }
                    className={clsx(
                      "text-xs px-2 py-1 rounded transition-colors",
                      citation.page_number
                        ? "bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer"
                        : "bg-gray-200 text-gray-600",
                    )}
                    title={citation.excerpt}
                  >
                    {citation.page_number
                      ? `Page ${citation.page_number}`
                      : `Source ${index + 1}`}
                  </button>
                ),
              )}
            </div>
          </div>
        )}

        {/* Feedback */}
        {!isUser && onFeedback && (
          <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-2">
            <span className="text-xs text-gray-500">Was this helpful?</span>
            <button
              onClick={() => onFeedback(message.id, "up")}
              className={clsx(
                "p-1 rounded transition-colors",
                message.feedback_rating === "up"
                  ? "text-green-600"
                  : "text-gray-400 hover:text-green-600",
              )}
            >
              {message.feedback_rating === "up" ? (
                <HandThumbUpSolidIcon className="h-4 w-4" />
              ) : (
                <HandThumbUpIcon className="h-4 w-4" />
              )}
            </button>
            <button
              onClick={() => onFeedback(message.id, "down")}
              className={clsx(
                "p-1 rounded transition-colors",
                message.feedback_rating === "down"
                  ? "text-red-600"
                  : "text-gray-400 hover:text-red-600",
              )}
            >
              {message.feedback_rating === "down" ? (
                <HandThumbDownSolidIcon className="h-4 w-4" />
              ) : (
                <HandThumbDownIcon className="h-4 w-4" />
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
